/**
 * TextRenderer — creates a PixiJS Text display object from text-renderer component data.
 *
 * Maps text-renderer component properties (fontFamily, fontSize, color, etc.)
 * to a PixiJS TextStyle and renders a Text object inside a Container.
 */
import { Container, Text, TextStyle } from 'pixi.js';
import type { GameObject } from '@core/types/gameobject.types';

export class TextRenderer {
  /** Create a PixiJS container with a Text child. */
  static create(gameObject: GameObject): Container {
    const container = new Container();
    const textData = gameObject.components.find(c => c.type === 'text-renderer')
      ?.data as Record<string, unknown> | undefined;

    const content = (textData?.text as string) ?? 'Text';
    const style = TextRenderer.buildStyle(textData);

    const text = new Text({ text: content, style });
    text.anchor.set(gameObject.transform.anchor.x, gameObject.transform.anchor.y);
    container.addChild(text);

    return container;
  }

  /** Update an existing text container with new game object state. */
  static update(container: Container, gameObject: GameObject): void {
    const textData = gameObject.components.find(c => c.type === 'text-renderer')
      ?.data as Record<string, unknown> | undefined;

    // Find existing Text child
    const existingText = container.children.find(c => c instanceof Text) as Text | undefined;

    if (existingText) {
      const content = (textData?.text as string) ?? 'Text';
      existingText.text = content;
      existingText.style = TextRenderer.buildStyle(textData);
      existingText.anchor.set(
        gameObject.transform.anchor.x,
        gameObject.transform.anchor.y
      );
    } else {
      // No text child — recreate
      const newContainer = TextRenderer.create(gameObject);
      for (const child of newContainer.children.slice()) {
        container.addChild(child);
      }
    }
  }

  /** Build a PixiJS TextStyle from component data. */
  private static buildStyle(textData: Record<string, unknown> | undefined): TextStyle {
    return new TextStyle({
      fontFamily: (textData?.fontFamily as string) ?? 'Inter',
      fontSize: (textData?.fontSize as number) ?? 16,
      fontWeight: (textData?.fontWeight as string) ?? 'normal',
      fontStyle: (textData?.fontStyle as string) ?? 'normal',
      fill: (textData?.fill as string) ?? (textData?.color as string) ?? '#ffffff',
      align: (textData?.textAlign as string) ?? 'left',
      wordWrap: (textData?.wordWrap as boolean) ?? false,
      wordWrapWidth: (textData?.wordWrapWidth as number) ?? 200,
      letterSpacing: (textData?.letterSpacing as number) ?? 0,
      lineHeight: (textData?.lineHeight as number) ?? 0,
    });
  }
}
